import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Checkbox, Descriptions, Divider, Flex, Grid, Skeleton, Tooltip, Typography } from 'antd';
import type { CollapseProps } from 'antd';
import dayjs from 'dayjs';
import Parser from 'html-react-parser';
import { Fragment, useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getLeaveTicket, hrConfirmLeaveTicket } from 'src/apis/hr_ticket/leaveTicket.api';
import { BtnHrHandle } from 'src/components/v2/Button';
import CollapseCustom from 'src/components/v2/Collapse';
import { HRTicketStatusTag } from 'src/components/v2/Tag/TicketStatusTag/TicketStatusTag';
import { ConfirmTypeEn } from 'src/constants/confirmTypeEn.enum';
import urlPath from 'src/constants/urlPath';
import { AppContext } from 'src/contexts/app.context';
import { HrTicketDetailContext } from 'src/contexts/hrTicketDetail.context';
import { TicketContext } from 'src/contexts/ticket.context';
import { useBasePath } from 'src/hooks/useBasePath';
import type { ILeaveTicketDetail } from 'src/types/hr_ticket/leaveTicket.type';
import { showNotification } from 'src/utils/notification';
import { formatDate } from 'src/utils/utils';
import './LeaveTicketDetail.scss';
const { Title, Text } = Typography;

type HrConfirmResponses = {
  ticketId: number;
  isHrConfirm: ConfirmTypeEn;
  unconfirmedDetails: number[];
  note?: string;
};

export default function LeaveTicketDetail() {
  const { setLeaveTicket, setLoading } = useContext(HrTicketDetailContext);
  const { setInvalidKey } = useContext(TicketContext);
  const { ticketSelected } = useContext(AppContext);
  const { id } = useParams();
  const queryClient = useQueryClient();
  const basePath = useBasePath();
  const [leaveTicketDetails, setLeaveTicketDetails] = useState<ILeaveTicketDetail[]>([]);

  const { useBreakpoint } = Grid;
  const breakpoints = useBreakpoint();
  const colspan = breakpoints.xxl ? 2 : 1;

  const { data, isFetching } = useQuery({
    queryKey: ['leaveTicket', id],
    queryFn: () => getLeaveTicket(Number(id)),
    enabled: !!id && !!ticketSelected
  });

  const hrConfirmPath = basePath === urlPath.hrConfirmLeaveTicket.location;

  const isShowNotHrConfirm = hrConfirmPath && data?.data.hrConfirmedType === ConfirmTypeEn.WaitingConfirm;

  useEffect(() => {
    if (data) {
      setLeaveTicket(data);
      setLeaveTicketDetails(data.data.leaveTicketDetails);
      setLoading(isFetching);
      setInvalidKey(JSON.stringify(data?.data));
    }
  }, [data, isFetching, setLeaveTicket, setLoading, setInvalidKey]);

  function RenderBioLog(inTime: Date, outTime: Date) {
    const currentMessageDate = dayjs(outTime).format('YYYY-MM-DD HH:mm:ss');
    const previousMessageDate = dayjs(inTime).format('YYYY-MM-DD HH:mm:ss');

    const timeDifference = (Date.parse(currentMessageDate) - Date.parse(previousMessageDate)) / (1000 * 60 * 60);

    if (timeDifference > 3) {
      return true;
    } else {
      return false;
    }
  }

  const leaveTicketDetailItems = (leaveTicketDetails: ILeaveTicketDetail[]): CollapseProps['items'] => {
    return leaveTicketDetails.map((leaveTicketDetail) => ({
      key: leaveTicketDetail.id,
      label: (
        <Descriptions
          className='leave-ticket-item-header'
          layout='horizontal'
          labelStyle={{ width: '135px' }}
          column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
        >
          <Descriptions.Item label='Người nghỉ'>
            <Text strong>{leaveTicketDetail?.employee?.fullName}</Text>
          </Descriptions.Item>
          <Descriptions.Item label='Ngày nghỉ'>
            <Text strong>{formatDate(leaveTicketDetail.date)}</Text>
          </Descriptions.Item>
          <Descriptions.Item label='Loại phép'>{leaveTicketDetail.leaveType.name}</Descriptions.Item>
          <Descriptions.Item label='Thời gian nghỉ'>{leaveTicketDetail.leaveStatus.name}</Descriptions.Item>
          <Descriptions.Item label='Phép tồn'>{leaveTicketDetail.leaveRemaining}</Descriptions.Item>
          {leaveTicketDetail?.biometricDataLogs?.length > 0 && (
            <Descriptions.Item label='Chấm công'>
              <Flex gap={20}>
                <Text>
                  <Text type='secondary'>Giờ vào: </Text>
                  <Text type='success'>{dayjs(leaveTicketDetail?.biometricDataLogs[0]?.record).format('HH:mm')}</Text>
                </Text>
                {RenderBioLog(
                  leaveTicketDetail?.biometricDataLogs[0]?.record,
                  leaveTicketDetail?.biometricDataLogs[leaveTicketDetail?.biometricDataLogs?.length - 1].record
                ) && (
                  <Text>
                    <Text type='secondary'>Giờ ra: </Text>
                    <Text type='success'>
                      {dayjs(
                        leaveTicketDetail?.biometricDataLogs[leaveTicketDetail?.biometricDataLogs?.length - 1]?.record
                      ).format('HH:mm')}
                    </Text>
                  </Text>
                )}
              </Flex>
            </Descriptions.Item>
          )}
          <Descriptions.Item label='Phiếu liên quan'>
            {leaveTicketDetail.leaveTicketDetailAttaches?.map((item, index) => (
              <Text key={index}>
                {index > 0 && ', '}
                <Link to={`${urlPath.myTicket.location}/${item.overtimeTicketId}`}>{item?.overtimeTicket?.code}</Link>
              </Text>
            ))}
          </Descriptions.Item>
        </Descriptions>
      ),
      className: `leave-ticket-item ${hrConfirmPath && leaveTicketDetail.isHrUnconfirmed && 'hr-not-confirm'}`,
      showArrow: false,
      collapsible: 'icon',
      extra: isShowNotHrConfirm ? (
        <Tooltip
          title='Trường hợp người đăng ký nghỉ phép nhưng lại có đi làm, bạn có thể bỏ qua việc xác nhận.'
          color='rgb(79, 96, 107)'
          arrow={false}
        >
          <Checkbox
            checked={leaveTicketDetail.isHrUnconfirmed}
            value={leaveTicketDetail.isHrUnconfirmed}
            onChange={() => {
              handleHrUnConfirm(leaveTicketDetail.isHrUnconfirmed, Number(leaveTicketDetail.id));
            }}
            className='hr-not-confirm-icon'
          >
            <Text style={{ fontWeight: 500 }}>Bỏ qua xác nhận</Text>
          </Checkbox>
        </Tooltip>
      ) : (
        leaveTicketDetail.isHrUnconfirmed && (
          <Text type='danger' className='hr-not-confirm-flag'>
            Đã từ chối
          </Text>
        )
      )
    }));
  };

  const handleHrUnConfirm = (checked: boolean, leaveTicketDetailId: number) => {
    const cloneLeaveTicketDetails = [...leaveTicketDetails];
    const index = cloneLeaveTicketDetails.findIndex((x) => x.id === leaveTicketDetailId);
    cloneLeaveTicketDetails[index].isHrUnconfirmed = !checked;
    setLeaveTicketDetails(cloneLeaveTicketDetails);
  };

  const hrConfirmLeaveTicketMutation = useMutation({
    mutationFn: (rq: HrConfirmResponses) =>
      hrConfirmLeaveTicket(rq.ticketId, rq.isHrConfirm, rq.unconfirmedDetails, rq?.note),
    onSuccess: (_, rq) => {
      if (rq.isHrConfirm === ConfirmTypeEn.Confirmed) {
        showNotification(`Xác nhận phiếu thành công - ${data?.data.code}`, 'success');
        handleInvalidQueryData();
      }
    }
  });

  const handleInvalidQueryData = () => {
    const invalidateQueryKeys = ['statusCount', 'segmentedStatusCount', 'leaveTicket'];
    invalidateQueryKeys.forEach((queryKey) => {
      queryClient.invalidateQueries({
        queryKey: [queryKey]
      });
    });
  };

  const handleApprovalConfirm = () => {
    hrConfirmLeaveTicketMutation.mutate({
      ticketId: Number(data?.data.id),
      isHrConfirm: ConfirmTypeEn.Confirmed,
      unconfirmedDetails: leaveTicketDetails
        .filter(({ isHrUnconfirmed }) => isHrUnconfirmed === true)
        .map(({ id }) => Number(id))
    });
  };

  return (
    <div className='leave-ticket-detail-wrapper'>
      <BtnHrHandle type='leaveTicket' onClick={handleApprovalConfirm} />

      <Divider style={{ marginTop: 16, marginBottom: 16 }} />

      <Flex justify='start'>
        <Title level={5}>Thông tin phiếu</Title>
      </Flex>
      {isFetching ? (
        <Skeleton active />
      ) : (
        <Fragment>
          <Descriptions
            className='leave-ticket-item-header'
            layout='horizontal'
            labelStyle={{ width: '135px' }}
            column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
          >
            <Descriptions.Item label='Mã phiếu'>{data?.data?.code}</Descriptions.Item>
            <Descriptions.Item label='Trạng thái'>
              <HRTicketStatusTag
                ticketStatus={data?.data.ticketStatus}
                erpKey={data?.data.erpKey}
                hrConfirmedType={data?.data.hrConfirmedType}
              />
            </Descriptions.Item>
            <Descriptions.Item label='Người tạo'>{data?.data?.createdBy?.fullName}</Descriptions.Item>
            <Descriptions.Item label='Bộ phận'>{data?.data?.createdBy?.department?.name}</Descriptions.Item>
            <Descriptions.Item label='Người nghỉ'>
              <Text strong>{data?.data.leaveTicketDetails?.[0]?.employee?.fullName}</Text>
            </Descriptions.Item>
            <Descriptions.Item label='Bộ phận'>
              <div className='dept'>
                <span>{data?.data?.leaveTicketDetails?.[0]?.employee?.department?.name}</span>
                {data?.data?.leaveTicketDetails?.[0]?.employee?.department?.id !==
                  data?.data?.leaveTicketDetails?.[0]?.employee?.group?.id && (
                  <span>{data?.data?.leaveTicketDetails?.[0]?.employee?.group?.name}</span>
                )}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label='Người làm thay' span={colspan}>
              <Text strong>{data?.data.replacer?.name}</Text>
            </Descriptions.Item>

            <Descriptions.Item label='Ngày tạo' span={colspan}>
              {formatDate(data?.data?.createdDate)}
            </Descriptions.Item>
            <Descriptions.Item label='Số phiếu ERP' span={colspan}>
              {data?.data?.erpKey}
            </Descriptions.Item>
            <Descriptions.Item label='Tổng ngày nghỉ'>{data?.data?.total}</Descriptions.Item>
            <Descriptions.Item label='Tổng phép tồn'>{data?.data?.totalLeaveRemining}</Descriptions.Item>
            <Descriptions.Item label={'Lý do nghỉ phép'} span={colspan}>
              <Text strong italic>
                {Parser(data?.data?.note || '')}
              </Text>
            </Descriptions.Item>
          </Descriptions>

          <Divider style={{ marginTop: 16, marginBottom: 16 }} />
          <CollapseCustom
            isSegmented={false}
            title='Danh sách ngày nghỉ'
            titleLevel={5}
            items={leaveTicketDetailItems(leaveTicketDetails)}
            expandIconPosition='end'
            className='leave-ticket-list'
          />
        </Fragment>
      )}
    </div>
  );
}
